import { Providers } from "@app/providers"
import { registerAccessDeniedHandler } from "@app/providers/access-denied/register"
import { registerSubscriptions } from "@app/providers/websocket/subscriptions"
import { ErrorFallback } from "@app/ui"
import { wsBridge } from "@shared/api/websocket"
import { QueryCache, QueryClient } from "@tanstack/react-query"
import {
	type AnyRouter,
	createBrowserHistory,
	createHashHistory,
	createRouter as createTanstackRouter,
} from "@tanstack/react-router"
import type { Friend } from "@/entities/friend"
import { registerVoiceQueryDefaults } from "@/entities/voice-message"
import { QUERY_CONFIG, retryTransient } from "@/shared/config/query"
import { onSessionExpired, registerSessionCleanup } from "@/shared/lib/auth"
import { detectPlatform } from "@/shared/lib/platform"
import { PendingComponent } from "@/shared/ui/pending-component"
import { routeTree } from "./routeTree.gen"

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createRouter>
	}
	interface HistoryState {
		friend?: Friend
	}
	interface StaticDataRouteOption {
		manualPageview?: boolean
	}
}

export const createRouter = () => {
	const queryClient = new QueryClient({
		queryCache: new QueryCache({
			onError: (error, query) => {
				query.meta?.onError?.(error)
			},
		}),
		defaultOptions: {
			queries: {
				staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
				retry: retryTransient,
				networkMode: "always",
				refetchOnReconnect: true,
			},
			mutations: {
				networkMode: "always",
			},
		},
	})
	registerVoiceQueryDefaults(queryClient)

	const unregisterAccessDenied = registerAccessDeniedHandler(queryClient)
	if (import.meta.hot) {
		import.meta.hot.dispose(unregisterAccessDenied)
	}

	const unregisterCacheCleanup = registerSessionCleanup(async () => {
		try {
			await queryClient.cancelQueries()
		} finally {
			queryClient.clear()
		}
	})
	if (import.meta.hot) {
		import.meta.hot.dispose(unregisterCacheCleanup)
	}

	// На нативных Capacitor-платформах (ios/android) приложение сервится с локальной
	// схемы (capacitor://localhost / http://localhost), поэтому browser-history при
	// reload/restore пути `/foo` приводит к 404 → используем hash-history. На web и в
	// Telegram остаётся обычная browser-history.
	const platform = detectPlatform()
	const history =
		platform === "ios" || platform === "android"
			? createHashHistory()
			: createBrowserHistory()

	const router = createTanstackRouter({
		routeTree,
		history,
		context: {
			queryClient,
			userId: null,
			isAuthenticated: false,
			accessState: { kind: "anonymous" },
		},
		defaultPreload: "viewport",
		defaultPreloadStaleTime: 0,

		defaultPendingComponent: PendingComponent,
		defaultErrorComponent: ErrorFallback,
		// biome-ignore lint/correctness/noComponentHookFactories: createRouter runs once per app boot, so Wrap is created once and stays stable
		Wrap: ({ children }) => (
			<Providers queryClient={queryClient}>{children}</Providers>
		),
	})

	wsBridge.init(queryClient, router)
	registerSubscriptions()

	subscribeToSessionExpiry(router)

	return router
}

// The common session cleanup has already dropped account data. Re-run the
// root authentication check so route guards can act on the expired session.
function subscribeToSessionExpiry(router: AnyRouter): void {
	let isRecoveringSession = false
	const unsubscribe = onSessionExpired(() => {
		if (isRecoveringSession) return
		isRecoveringSession = true
		void router.invalidate().finally(() => {
			isRecoveringSession = false
		})
	})
	if (import.meta.hot) import.meta.hot.dispose(unsubscribe)
}
